import { baseApi, type ApiResponse } from './client';

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedData<T> {
  data: T[];
  pagination: Pagination;
}

export interface CatalogListParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
  is_default?: boolean;
  include_deleted?: boolean;
}

export interface GatewayType {
  id: number;
  name: string;
  code: string;
  slug: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  prefixSubChannel?: string;
  prefixPubChannel?: string;
  icon?: string;
  image?: string;
  colorCode?: string;
  mqtts?: Array<{ id: number; name: string; code: string; isDefault: boolean }>;
  defaultMqtt?: { id: number; name: string; code: string; isDefault: boolean };
  createdAt?: string;
  updatedAt?: string;
  mqttLinks?: Array<{ id: number; mqttId: number; isDefault: boolean; mqtt?: { id: number; name: string; code: string } }>;
}

export interface DeviceType {
  id: number;
  name: string;
  code: string;
  slug: string;
  description?: string;
  frame?: string;
  baudRate?: number;
  isDefault: boolean;
  isActive: boolean;
  isDeleted?: boolean;
  registers?: DeviceTypeRegister[];
  createdAt?: string;
  updatedAt?: string;
}

export interface DeviceTypeRegisterMapping {
  id?: number;
  value: string;
  label: string;
  description?: string;
}

export interface DeviceTypeRegister {
  id?: number;
  orderNo: number;
  code: string;
  name: string;
  functionCode: number;
  startAddress: number;
  numOfRegister: number;
  dataType: number;
  decimal?: number;
  unit?: string;
  formula?: string;
  aggregateFunction?: number;
  shiftBytes?: number;
  isSelected?: boolean;
  mappings?: DeviceTypeRegisterMapping[];
}

export interface MqttConfig {
  id: number;
  name: string;
  code: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface GatewayTypePayload {
  name: string;
  code: string;
  slug: string;
  description?: string;
  is_default?: boolean;
  prefix_sub_channel?: string;
  prefix_pub_channel?: string;
  icon?: string;
  image?: string;
  color_code?: string;
  mqtt_ids?: number[];
  default_mqtt_id?: number;
  is_active?: boolean;
}

export interface DeviceTypePayload {
  name: string;
  code: string;
  slug: string;
  description?: string;
  frame?: string;
  baud_rate?: number;
  is_default?: boolean;
  is_active?: boolean;
  registers?: DeviceTypeRegister[];
}

export const adminCatalogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMqttConfigs: builder.query<ApiResponse<PaginatedData<MqttConfig>>, CatalogListParams | void>({
      query: (params) => ({ url: '/admin/mqtt', method: 'GET', params: params || {} }),
      providesTags: [{ type: 'Settings', id: 'MQTT_LIST' }],
    }),
    getMqttConfig: builder.query<ApiResponse<MqttConfig>, string | number>({
      query: (id) => `/admin/mqtt/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Settings', id }],
    }),
    createMqttConfig: builder.mutation<ApiResponse<MqttConfig>, { name: string; code: string }>({
      query: (body) => ({ url: '/admin/mqtt', method: 'POST', body }),
      invalidatesTags: [{ type: 'Settings', id: 'MQTT_LIST' }],
    }),
    updateMqttConfig: builder.mutation<ApiResponse<MqttConfig>, { id: string | number; data: { name?: string; code?: string; is_active?: boolean } }>({
      query: ({ id, data }) => ({ url: `/admin/mqtt/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: [{ type: 'Settings', id: 'MQTT_LIST' }],
    }),
    deleteMqttConfig: builder.mutation<ApiResponse<void>, string | number>({
      query: (id) => ({ url: `/admin/mqtt/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Settings', id: 'MQTT_LIST' }],
    }),
    restoreMqttConfig: builder.mutation<ApiResponse<MqttConfig>, string | number>({
      query: (id) => ({ url: `/admin/mqtt/${id}/restore`, method: 'POST' }),
      invalidatesTags: [{ type: 'Settings', id: 'MQTT_LIST' }],
    }),
    toggleMqttConfig: builder.mutation<ApiResponse<MqttConfig>, string | number>({
      query: (id) => ({ url: `/admin/mqtt/${id}/toggle-active`, method: 'POST' }),
      invalidatesTags: [{ type: 'Settings', id: 'MQTT_LIST' }],
    }),
    getGatewayTypes: builder.query<ApiResponse<PaginatedData<GatewayType>>, CatalogListParams | void>({
      query: (params) => ({
        url: '/admin/gateway-types',
        method: 'GET',
        params: params || {},
      }),
      providesTags: (result) => [
        ...(result?.result?.data || []).map((item) => ({ type: 'GatewayTypes' as const, id: item.id })),
        { type: 'GatewayTypes' as const, id: 'LIST' },
      ],
    }),
    getGatewayType: builder.query<ApiResponse<GatewayType>, string | number>({
      query: (id) => `/admin/gateway-types/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'GatewayTypes', id }],
    }),
    createGatewayType: builder.mutation<ApiResponse<GatewayType>, GatewayTypePayload>({
      query: (body) => ({ url: '/admin/gateway-types', method: 'POST', body }),
      invalidatesTags: [{ type: 'GatewayTypes', id: 'LIST' }],
    }),
    updateGatewayType: builder.mutation<
      ApiResponse<GatewayType>,
      { id: string | number; data: Partial<GatewayTypePayload> }
    >({
      query: ({ id, data }) => ({ url: `/admin/gateway-types/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'GatewayTypes', id },
        { type: 'GatewayTypes', id: 'LIST' },
      ],
    }),
    deleteGatewayType: builder.mutation<ApiResponse<void>, string | number>({
      query: (id) => ({ url: `/admin/gateway-types/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'GatewayTypes', id: 'LIST' }],
    }),
    toggleGatewayType: builder.mutation<ApiResponse<GatewayType>, string | number>({
      query: (id) => ({ url: `/admin/gateway-types/${id}/toggle-active`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'GatewayTypes', id },
        { type: 'GatewayTypes', id: 'LIST' },
      ],
    }),
    setDefaultGatewayType: builder.mutation<ApiResponse<GatewayType>, string | number>({
      query: (id) => ({ url: `/admin/gateway-types/${id}/set-default`, method: 'POST' }),
      invalidatesTags: [{ type: 'GatewayTypes', id: 'LIST' }],
    }),

    getDeviceTypes: builder.query<ApiResponse<PaginatedData<DeviceType>>, CatalogListParams | void>({
      query: (params) => ({
        url: '/admin/device-types',
        method: 'GET',
        params: params || {},
      }),
      providesTags: (result) => [
        ...(result?.result?.data || []).map((item) => ({ type: 'DeviceTypes' as const, id: item.id })),
        { type: 'DeviceTypes' as const, id: 'LIST' },
      ],
    }),
    getDeviceType: builder.query<ApiResponse<DeviceType>, string | number>({
      query: (id) => `/admin/device-types/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'DeviceTypes', id }],
    }),
    restoreDeviceType: builder.mutation<ApiResponse<DeviceType>, string | number>({
      query: (id) => ({ url: `/admin/device-types/${id}/restore`, method: 'POST' }),
      invalidatesTags: [{ type: 'DeviceTypes', id: 'LIST' }],
    }),
    createDeviceType: builder.mutation<ApiResponse<DeviceType>, DeviceTypePayload>({
      query: (body) => ({ url: '/admin/device-types', method: 'POST', body }),
      invalidatesTags: [{ type: 'DeviceTypes', id: 'LIST' }],
    }),
    updateDeviceType: builder.mutation<
      ApiResponse<DeviceType>,
      { id: string | number; data: Partial<DeviceTypePayload> }
    >({
      query: ({ id, data }) => ({ url: `/admin/device-types/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'DeviceTypes', id },
        { type: 'DeviceTypes', id: 'LIST' },
      ],
    }),
    deleteDeviceType: builder.mutation<ApiResponse<void>, string | number>({
      query: (id) => ({ url: `/admin/device-types/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'DeviceTypes', id: 'LIST' }],
    }),
    toggleDeviceType: builder.mutation<ApiResponse<DeviceType>, string | number>({
      query: (id) => ({ url: `/admin/device-types/${id}/toggle-active`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'DeviceTypes', id },
        { type: 'DeviceTypes', id: 'LIST' },
      ],
    }),
    setDefaultDeviceType: builder.mutation<ApiResponse<DeviceType>, string | number>({
      query: (id) => ({ url: `/admin/device-types/${id}/set-default`, method: 'POST' }),
      invalidatesTags: [{ type: 'DeviceTypes', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetMqttConfigsQuery,
  useGetMqttConfigQuery,
  useCreateMqttConfigMutation,
  useUpdateMqttConfigMutation,
  useDeleteMqttConfigMutation,
  useRestoreMqttConfigMutation,
  useToggleMqttConfigMutation,
  useGetGatewayTypesQuery,
  useGetGatewayTypeQuery,
  useCreateGatewayTypeMutation,
  useUpdateGatewayTypeMutation,
  useDeleteGatewayTypeMutation,
  useToggleGatewayTypeMutation,
  useSetDefaultGatewayTypeMutation,
  useGetDeviceTypesQuery,
  useGetDeviceTypeQuery,
  useRestoreDeviceTypeMutation,
  useCreateDeviceTypeMutation,
  useUpdateDeviceTypeMutation,
  useDeleteDeviceTypeMutation,
  useToggleDeviceTypeMutation,
  useSetDefaultDeviceTypeMutation,
} = adminCatalogApi;
